import { createSelector } from "@ngrx/store";
import { allAssociatedAssetsIdsReselector } from './entity.reselector';
import { sessionMapReselector } from './sessions.reselector';
import { AttendeeTableData } from "../../helpers/table-column-types";
import { getBaseAttendeeTableData, attendeeListReselector } from "./entity.decircular.reselector";
import { fullSessionsEntitiesReselector, getActiveLights, getBooleanLight, NestedProgramInfo, SessionsEntitiesData, sortByDateAndStart } from "./performerSponsors.reselector";
import { environment } from "../../../environments/environment";
import { shareUrlsByType } from "globals/store/share.data";
import { getAttendeeBriefActions } from "../share.brief.functions";
import { tableLightColors } from "globals/helpers/table-helpers.data";
import { getMediaTermsLights } from "./entity.reselector.helpers";
import { Attendee, filterAttendees } from "globals/store/attendees.data"
import { Session } from "globals/store/programs.data";
import { Dictionary } from "@ngrx/entity";
import { makeMapFromListOfObjectsAndKey } from "globals/helpers/helpers";

const workersByEmail = (store) => store.workforce.workersByEmail
const assets = (store)=> store.assets.entities
const labelMaps = (store) => store.events.labelMaps
const attendees = (store) => store.attendees.entities
const attendeeTableFilters = (store) => store.filters.attendees
const attendeeTypes = (store) => store.attendees.attendeeTypes
const attendeeProfileEditShareables = (store) => store.shareables.attendee
const attendeeBriefs = (store) => store.shareables.attendeeBrief
const event = (store) => store.events.event
const attendeeLabels = (store) => store.events.labelMaps.attendee_labels

const SearchableAttendeeFields = ['name', 'line1', 'line2']

export const attendeeBriefActionsReselector = createSelector(
  attendeeProfileEditShareables,
  event,
  getAttendeeBriefActions
)


export const attendeeTablePreselector = createSelector( // a selector can only have 10 inputs at most
  workersByEmail,
  labelMaps,
  allAssociatedAssetsIdsReselector,
  sessionMapReselector,
  (workersByEmail, labelMaps, associatedAssetIdsByEntityId, sessions: { [id:string]: Session } ) => 
    ({ workersByEmail, labelMaps, associatedAssetIdsByEntityId, sessions })
)


export const sessionAttendeeReselector = createSelector( // for manage attendee
  fullSessionsEntitiesReselector,
  ({ sessionInfos }: SessionsEntitiesData, attendeeId: string) => {
    const sessionList = Object.values(sessionInfos)
    sessionList.sort(sortByDateAndStart)

    const unusedSessionIds = sessionList.filter(s => !s.attendeeTags.includes(attendeeId)).map(s => s.id)
    const sessionProgramList: NestedProgramInfo[] = []
    let programListIndex = 0
    const programIdToListLocation = {}
    unusedSessionIds.forEach(id => {
      const session = sessionInfos[id]
      const { programId, programTitle } = session
      if (!programIdToListLocation.hasOwnProperty(programId)) {
        programIdToListLocation[programId] = programListIndex++
        sessionProgramList.push({ programId, programTitle, sessionList: [] })
      }
      sessionProgramList[programIdToListLocation[programId]].sessionList.push({ ...session, selected: false })
    })

    return { sessionList: sessionList.filter(s => s.attendeeTags.includes(attendeeId)), sessionProgramList }
  }
)

export const attendeeTableReselector = createSelector(
  attendeeTablePreselector,
  attendeeListReselector,
  attendeeTypes,
  attendeeProfileEditShareables,
  attendeeBriefs,
  event,
  attendeeLabels,
  attendeeTableFilters,
  ({ workersByEmail, labelMaps, associatedAssetIdsByEntityId, sessions },
    attendeeList, attendeeTypes, shareables, briefs, event, attendeeLabels, attendeeTableFilters
  ) => {
    const { search } = attendeeTableFilters
    if (search) {
      const searchTerm = search.toLocaleLowerCase()
      attendeeList = attendeeList.filter(p => SearchableAttendeeFields.some(field => p[field] && p[field].toLocaleLowerCase().includes(searchTerm)))
    }
    attendeeList = filterAttendees(attendeeTableFilters, attendeeList);
    const sessionsList = Object.values(sessions);

    const tableData = attendeeList.map((a: Attendee) => {
      
      const { id, WFContact, confirmed, labels, entityTypeId } = a 
      const attendeeType = !entityTypeId || !attendeeTypes[entityTypeId] ? null : attendeeTypes[entityTypeId]
      const contact = workersByEmail[WFContact]
      const shareable = shareables[id]
      const sessionCount = sessionsList.filter(s => s.attendeeTags?.includes(id)).length

      const briefInfo = briefs[id]
      const briefActions = getAttendeeBriefActions(shareables, event, id)

      const briefOn = briefInfo && briefInfo.status === 'On'
      const hasActions = briefOn && !!briefActions?.total

      const tableData: AttendeeTableData = {
        ...getBaseAttendeeTableData(a),
        attendee: a,
        attendeeType: { rank: !attendeeType ? 0 : attendeeType.rank, text: !attendeeType ? '' : attendeeType.name },
        contact: !contact ? null : contact,
        sessions: { text: `${sessionCount}` },
        status: getBooleanLight(confirmed),
        publicEditProfileStatus: getActiveLights(!shareable ? null : shareable.status, !shareable ? null : environment.joi.shareBase + shareUrlsByType.attendee + '/' + shareable.code ),
        briefActions: !hasActions ? null : { percent: briefActions.submitted / briefActions.total, lights: [{ text: `${briefActions.submitted} / ${briefActions.total} completed`, color: briefActions.total === briefActions.submitted ? tableLightColors.green : tableLightColors.red }] },
        briefUrl: { url: briefOn ? environment.joi.shareBase + shareUrlsByType.attendeeBrief + '/' + briefInfo.id : null, percent: briefOn ? 1 : 0, lights: [{ text: briefOn ? 'On' : 'Off', color: briefOn ? tableLightColors.green : tableLightColors.gray }]},
        terms: !event ? null : getMediaTermsLights(event.mediaTerms.attendee, a, briefInfo),
        labels: labels ? labels.filter(label => attendeeLabels[label]).map(label => ({ color: attendeeLabels[label].color, label: attendeeLabels[label].label })) : [],
      }
      return tableData
    })
    const tableRowsById = makeMapFromListOfObjectsAndKey(tableData, 'id')
    return { tableData, tableRowsById }
  }
)